import React, { useState, useEffect, useCallback } from 'react';
import {
  GridRow,
  GridCol,
  Link,
  Button,
  Heading,
  BodyText,
  Card,
  List,
  ListItem,
  Notification
} from '@lmig/lmds-react';

import { observer } from 'mobx-react';
import { mfaStore as store } from '../store/mfa-store';
import EmptySpace from '../../common/components/empty-space/empty-space-component';
import ActionModal from '../../common/components/action-modal/action-modal-component';
import { useNavigate } from 'react-router-dom';
import { trackCustomHeapEvent } from '../../../utils/heap';
import { maskMfaTarget } from '../../../utils/common';
import PopoverComponent from '../../common/components/popover/popover-component';
import { Skeleton } from '@lmig/lmds-react-skeleton';
import styles from './settings-page.module.scss';

const Settings = observer(() => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    store.fetchMfaDetails();

    return () => {
      store.resetStore();
    };
  }, []);

  const back = useCallback(() => {
    navigate('/claim-search');
  }, [navigate]);

  const navigateLearnMore = useCallback(() => {
    navigate('/settings/learn-more');
  }, [navigate]);

  const handleResetMfa = async () => {
    await store.resetMfa();
    setIsOpen(false);
    if (store.resetStatus?.success) {
      trackCustomHeapEvent('reset-mfa-success', {});
      navigate('/settings/success');
      return;
    }
    if (store.resetError) {
      trackCustomHeapEvent('reset-mfa-service-error', store.resetError);
    }
  };

  return (
    <GridRow>
      <GridCol className={styles.settingsPage}>
        <EmptySpace height={32} />
        <GridRow gutters gutterSize='fixed-16'>
          <GridCol>
            <Link
              caretPosition="left"
              variant="standalone"
              onClick={() => back()}
              aria-label='Back'
            >
              Back
            </Link>
          </GridCol>
          {store.resetError && <GridCol base={12} className={styles.errorContainer}>
            <Notification
              alert="There was an error resetting your multifactor authentication. Please try again."
              highlightType="negative"
            />
          </GridCol>}
        </GridRow>
        <GridCol lg={6} xl={6}>
          <Heading type="h2-light" className={styles.heading}>My account settings</Heading>
          <Heading type="h4-bold" className={styles.subHeading}>Update identity authentication method</Heading>
          <Link
            variant="standalone"
            onClick={() => navigateLearnMore()}
          >
            Learn more about how we keep your account secure
          </Link>
          <EmptySpace height={24} />
          <Card elevation="raised">
            <BodyText type='default'>Multifactor authentication method
              <PopoverComponent data-testid="mfa-popover">
                Your multifactor authentication method is how we send your confirmation codes before you log in to your account. These codes are used as an extra security measure to protect your identity.
              </PopoverComponent>
            </BodyText>
            {store.loading ? <Skeleton /> :
              <>
                <Heading className={styles.mfaKindHeading} type="h5">{store.mfaDetails?.kind}</Heading>
                <Heading className={styles.mfaDetails} type="h5">{maskMfaTarget(store.mfaDetails?.target ?? '')}</Heading>
              </>}
            <EmptySpace height={8} />
            <BodyText className={styles.boldParagraph}>Can I turn off multifactor authentication?</BodyText>
            <BodyText type='default' className={styles.subText}>Multifactor authentication is required by law to keep your account secure, so it cannot be turned off.</BodyText>
            <BodyText className={styles.boldParagraph}>Need to change this info?</BodyText>
            <BodyText type='default' className={styles.subText}>You’ll need to reset your multifactor authentication method (how you confirm your identity).</BodyText>
            <BodyText className={styles.boldParagraph}>How it works:</BodyText>
            <List className={styles.orderedList}>
              <ListItem>We’ll log you out of your account.</ListItem>
              <ListItem>When you log back in, you can choose a new, preferred identity confirmation method.</ListItem>
              <ListItem>This page will update with your new preferences.</ListItem>
            </List>
            <GridRow>
              <GridCol base={12} md={12} lg={12}>
                <Button
                  size="large"
                  variant="secondary"
                  data-testid="reset-mfa-method"
                  onClick={() => {
                    store.clearResetError();
                    setIsOpen(true);
                  }}
                  aria-label='Reset multifactor authentication'
                  disabled={store.loading || store.disableResetButton}
                >
                  Reset multifactor authentication
                </Button>
              </GridCol>
            </GridRow>
          </Card>
        </GridCol>
        <EmptySpace height={100} />
        <ActionModal
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          action={handleResetMfa}
          title='Are you sure you want to reset multifactor authentication. You will be logged out and need to re-register it.'
          cancelLabel='Cancel'
          actionLabel='Yes, reset and log out'
          loading={store.resetLoading}
        />
      </GridCol>
    </GridRow>
  );
});
export default Settings;
